import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, User, Home as HomeIcon, LogOut, LayoutDashboard, Shield, Building2, UserSquare2, Compass, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth, hasProfessionalAccess } from '../context/AuthContext';
import NotificationsBell from './NotificationsBell';
import AdvisorNavLink from './advisor/AdvisorNavLink';

const NAV = [
  { label: 'Comprar', to: '/buscar?op=venta' },
  { label: 'Arrendar', to: '/buscar?op=arriendo' },
  { label: 'Vender', to: '/publicar' },
  { label: 'Valora tu inmueble', to: '/valorar-gratis', highlight: true },
  { label: 'Asesores', to: '/agentes' },
  { label: 'Inmobiliarias', to: '/inmobiliarias' },
  { label: 'Proyectos', to: '/proyectos' },
  { label: 'Precios', to: '/precios' },
];

export default function Header({ transparent = false }) {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menu) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menu]);

  return (
    <header className={`${transparent ? 'bg-transparent absolute' : 'bg-white border-b border-gray-100'} top-0 left-0 right-0 z-40 w-full`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-habita flex items-center justify-center">
            <HomeIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-serif font-bold text-2xl tracking-tight text-habita">habita<span className="text-amber-500">.</span><span className="text-gray-800">co</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map(item => (
            <Link
              key={item.label}
              to={item.to}
              data-testid={item.highlight ? 'header-vende-con-ia' : undefined}
              className={item.highlight
                ? 'ml-1 px-4 py-2 text-sm font-semibold text-habita bg-habita-mint/70 rounded-full hover:bg-habita-mint transition-colors flex items-center gap-1.5'
                : 'px-4 py-2 text-sm font-medium text-gray-700 hover:text-habita transition-colors'}
            >
              {item.highlight && <Sparkles className="w-3.5 h-3.5" />}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <AdvisorNavLink />
          {user ? (
            <>
              <NotificationsBell />
              <div className="relative" ref={menuRef}>
              <button onClick={() => setMenu(!menu)} className="flex items-center gap-2 pl-1 pr-3 h-10 rounded-full hover:bg-gray-100 transition-colors">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-habita-mint flex items-center justify-center text-habita font-bold text-sm">{user.name?.[0]?.toUpperCase()}</div>
                )}
                <span className="text-sm font-medium text-gray-700">{user.name?.split(' ')[0]}</span>
              </button>
              {menu && (
                <div className="absolute right-0 top-12 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-semibold text-sm text-habita-dark truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-habita-mint text-habita">{user.role}</span>
                  </div>
                  <Link to="/mi-cuenta" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50"><LayoutDashboard className="w-4 h-4" /> Mi cuenta</Link>
                  {hasProfessionalAccess(user) && (
                    <Link to="/mi-cuenta/perfil-profesional" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="header-agent-link">
                      <UserSquare2 className="w-4 h-4" /> Mi perfil profesional
                    </Link>
                  )}
                  {(user.role === 'agent' || user.role === 'agency' || user.role === 'admin') && (
                    <Link to="/mi-cuenta/inmobiliaria" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="header-agency-link">
                      <Building2 className="w-4 h-4" /> Mi inmobiliaria
                    </Link>
                  )}
                  {user.role === 'developer' && (
                    <Link to="/mi-constructora" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="header-developer-link">
                      <Building2 className="w-4 h-4" /> Mi constructora
                    </Link>
                  )}
                  <Link to="/mi-decision" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="header-workspace-link"><Compass className="w-4 h-4" /> Mi decisión</Link>
                  <Link to="/publicar" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50"><HomeIcon className="w-4 h-4" /> Publicar propiedad</Link>
                  {hasProfessionalAccess(user) && (
                    <Link to="/crm" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="header-crm-link">
                      <UserSquare2 className="w-4 h-4" /> CRM · Leads
                    </Link>
                  )}
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50"><Shield className="w-4 h-4" /> Panel admin</Link>
                  )}
                  <button onClick={() => { setMenu(false); logout(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 border-t border-gray-100 mt-1 text-rose-600">
                    <LogOut className="w-4 h-4" /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
            </>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-habita transition-colors">Iniciar sesión</Link>
              <Button onClick={() => navigate('/registro')} className="btn-habita rounded-full px-5 h-10 text-sm font-semibold">
                Crear cuenta
              </Button>
            </>
          )}
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-4 space-y-1">
            <AdvisorNavLink mobile onClick={() => setOpen(false)} />
            {NAV.map(item => (
              <Link key={item.label} to={item.to} onClick={() => setOpen(false)} className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">{item.label}</Link>
            ))}
            {user ? (
              <>
                <Link to="/mi-cuenta" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">Mi cuenta</Link>
                <button onClick={() => { setOpen(false); logout(); }} className="w-full text-left block px-4 py-3 text-sm font-medium text-rose-600 hover:bg-gray-50 rounded-lg">Cerrar sesión</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">Iniciar sesión</Link>
                <Button onClick={() => { setOpen(false); navigate('/registro'); }} className="btn-habita w-full mt-2 rounded-full h-11">Crear cuenta</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
